import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTheme } from '../../contexts/useTheme';

interface MapSplitterProps {
  onPositionChange: (position: number) => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
}

/**
 * A draggable vertical splitter component for resizing the split map view.
 * Allows users to adjust the width ratio between left and right maps.
 */
export const MapSplitter = ({
  onPositionChange,
  containerRef,
}: MapSplitterProps) => {
  const { theme } = useTheme();
  const [isDragging, setIsDragging] = useState(false);
  const splitterRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      
      const containerRect = containerRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const mouseX = e.clientX - containerRect.left;
      
      // Calculate percentage (constrain between 25% and 75%)
      let newPosition = (mouseX / containerWidth) * 100;
      newPosition = Math.max(25, Math.min(75, newPosition));
      
      onPositionChange(newPosition);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, containerRef, onPositionChange]);

  return (
    <>
      {/* Overlay to capture mouse events during drag */}
      {isDragging && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            cursor: 'col-resize',
          }}
        />
      )}
      
      {/* Splitter handle */}
      <div
        ref={splitterRef}
        onMouseDown={handleMouseDown}
        style={{
          width: '8px',
          height: '100%',
          backgroundColor: isDragging 
            ? (theme === 'dark' ? 'rgba(24, 144, 255, 0.5)' : 'rgba(24, 144, 255, 0.5)')
            : (theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'),
          cursor: 'col-resize',
          flexShrink: 0,
          position: 'relative',
          transition: isDragging ? 'none' : 'background-color 0.2s',
          zIndex: 10,
        }}
        onMouseEnter={(e) => {
          if (!isDragging) {
            e.currentTarget.style.backgroundColor = theme === 'dark' 
              ? 'rgba(255, 255, 255, 0.2)' 
              : 'rgba(0, 0, 0, 0.2)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isDragging) {
            e.currentTarget.style.backgroundColor = theme === 'dark' 
              ? 'rgba(255, 255, 255, 0.1)' 
              : 'rgba(0, 0, 0, 0.1)';
          }
        }}
      >
        {/* Visual grip indicator */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '4px',
            height: '40px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'center',
            pointerEvents: 'none',
          }}
        >
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              style={{
                width: '4px',
                height: '4px',
                borderRadius: '50%',
                backgroundColor: theme === 'dark' 
                  ? 'rgba(255, 255, 255, 0.4)' 
                  : 'rgba(0, 0, 0, 0.3)',
              }}
            />
          ))}
        </div>
      </div>
    </>
  );
};
